import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  CRMFieldLayoutApiService,
  CRMMultiLocationFieldLayoutApiService,
  CrmObjectInterface,
  FieldGroupInterface as CrmFieldGroupInterface,
  FieldSchema,
  FieldSchemaInterface as CrmFieldSchemaInterface,
  FieldType,
  FieldValueInterface as CrmFieldValueInterface,
  ListFieldGroupsResponseInterface,
  ListFieldSchemaResponseInterface,
  ObjectProjectionFilterInterface,
  PagedRequestOptionsInterface,
  ValidateCrmObjectResponseValidationErrorInterface,
  ValidateCrmObjectResponseValidationErrorSeverity as ErrorSeverity,
} from '@vendasta/crm';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  expand,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  reduce,
  shareReplay,
  switchMap,
  take,
} from 'rxjs';
import { FieldValueWithTimer, isTimerFieldType } from '@galaxy/crm/services/adapter-service';
import { CrmObjectService } from './crm-object-service/crm-object.service';
import { suffixPotentialContactFieldGroupIds } from './potential-contact-field-suffix';
import {
  CrmDependencies,
  CrmInjectionToken,
  CrmMultiLocationDependencies,
  CrmMultilocationInjectionToken,
  FieldGroupInterface,
  FieldInterface,
  FieldSchemaInterface,
  FieldValueInterface,
  MultiLocationContext,
  ObjectType,
} from '../../tokens-and-interfaces';
import { isModifiedFieldValue } from './crm-form.utils';
import { TranslateForCrmObjectService } from '../../i18n';

const MAX_SERVER_PAGE_SIZE = 200;

interface FieldGroupsTarget {
  namespace: string;
  multiLocationNamespaces: string[];
}

export interface ValidateErrorsWithFieldSchemas {
  errors: ValidateCrmObjectResponseValidationErrorInterface[];
  fieldSchemaMap: Map<string, FieldSchemaInterface>;
}

/** Thrown when the server rejects a CRM object; carries the errors so forms can show them on their fields. */
export class CrmObjectValidationError extends Error {
  constructor(
    message: string,
    readonly validationErrors: ValidateCrmObjectResponseValidationErrorInterface[],
  ) {
    super(message);
  }
}

@Injectable()
export class CrmFormService {
  private readonly fieldGroupsRequestMap = new Map<string, Observable<CrmFieldGroupInterface[]>>();

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    @Inject(CrmMultilocationInjectionToken) private multiLocationConfig: CrmMultiLocationDependencies,
    private readonly crmObjectService: CrmObjectService,
    private readonly crmFieldLayoutApiService: CRMFieldLayoutApiService,
    private readonly crmMultiLocationFieldLayoutService: CRMMultiLocationFieldLayoutApiService,
    private readonly translationService: TranslateService,
    private readonly translateForCrmObjectService: TranslateForCrmObjectService,
  ) {}

  private generateRequestKey(target: FieldGroupsTarget, objectType: ObjectType, objectSubtype?: string): string {
    const scope = target.multiLocationNamespaces.length
      ? `ml:${target.multiLocationNamespaces.join(',')}`
      : target.namespace;
    return objectSubtype ? `${scope}::${objectType}::${objectSubtype}` : `${scope}::${objectType}`;
  }

  private handleValidationErrors$(
    objectType: ObjectType,
    validationResponse: ValidateErrorsWithFieldSchemas,
    modifiedFieldIds?: string[],
  ): Observable<string> {
    const invalidSeverityErrors = (validationResponse.errors ?? []).filter(
      (e) => e.severity === ErrorSeverity.VALIDATION_ERROR_SEVERITY_INVALID,
    );

    if (invalidSeverityErrors.length) {
      let firstError = invalidSeverityErrors[0];

      // If we have modified field IDs, try to find an error for one of those fields first
      if (modifiedFieldIds && modifiedFieldIds.length > 0) {
        const modifiedFieldError = invalidSeverityErrors.find((error) => {
          const errorFieldId =
            error.details?.uniquenessCheck?.fieldId ||
            (error.details?.fieldIds && error.details.fieldIds.length > 0 ? error.details.fieldIds[0] : '');
          return modifiedFieldIds.includes(errorFieldId);
        });

        if (modifiedFieldError) {
          firstError = modifiedFieldError;
        } else {
          // No errors found for the modified fields, so don't throw an error
          return of('');
        }
      }

      //get the name of the subtype if it exists
      if (objectType === 'CustomObject') {
        return (
          this.config?.customobject?.singularObjectName$?.pipe(
            take(1),
            switchMap((customObjectTypeName) => {
              const fieldId =
                firstError.details?.uniquenessCheck?.fieldId ||
                (firstError.details?.fieldIds && firstError.details.fieldIds.length > 0
                  ? firstError.details.fieldIds[0]
                  : '') ||
                '';
              const firstErrorSchema = validationResponse.fieldSchemaMap.get(fieldId);
              if (firstError.message) {
                const fieldName = firstErrorSchema?.fieldName || '';
                const capitalizedFieldName = fieldName.charAt(0).toUpperCase() + fieldName.slice(1).toLowerCase();
                return this.translateForCrmObjectService.getTranslationForCrmObject(objectType, firstError?.message, {
                  label: capitalizedFieldName,
                  customObjectName: customObjectTypeName,
                });
              } else {
                return 'Error';
              }
            }),
          ) ?? of('')
        );
      } else {
        const fieldId =
          firstError.details?.uniquenessCheck?.fieldId ||
          (firstError.details?.fieldIds && firstError.details.fieldIds.length > 0
            ? firstError.details.fieldIds[0]
            : '') ||
          '';
        const firstErrorSchema = validationResponse.fieldSchemaMap.get(fieldId);
        if (firstError.message) {
          const fieldName = firstErrorSchema?.fieldName || '';
          const capitalizedFieldName = fieldName.charAt(0).toUpperCase() + fieldName.slice(1).toLowerCase();
          return this.translateForCrmObjectService.getTranslationForCrmObject(objectType, firstError?.message, {
            label: capitalizedFieldName,
          });
        } else {
          return of('Error');
        }
      }
    } else {
      return of('');
    }
  }

  /*
   * TODO: this will probably start to lag when there's a lot of fields.
   * We should probably change to using tabs for different sets of fields later.
   */
  listAllObjectFields$(objectType: ObjectType, objectSubtype?: string): Observable<CrmFieldSchemaInterface[]> {
    return of({
      fieldSchemas: [],
      pagingMetadata: {
        nextCursor: '',
        hasMore: true,
      },
    }).pipe(
      expand((resp: ListFieldSchemaResponseInterface) => {
        if (!resp?.pagingMetadata?.hasMore) {
          return EMPTY;
        }
        return this.listObjectFieldsNextPage$(
          objectType,
          {
            cursor: resp.pagingMetadata.nextCursor,
            pageSize: MAX_SERVER_PAGE_SIZE,
          },
          objectSubtype,
        );
      }),
      reduce((acc: CrmFieldSchemaInterface[], val) => [...acc, ...(val?.fieldSchemas || [])], []),
    );
  }

  private listObjectFieldsNextPage$(
    objectType: ObjectType,
    paging: PagedRequestOptionsInterface,
    objectSubtype?: string,
  ): Observable<ListFieldSchemaResponseInterface> {
    return this.crmObjectService.listFieldSchemas(objectType, objectSubtype, paging);
  }

  /*
   * TODO: this will probably start to lag when there's a lot of fields.
   * We should probably change to using tabs for different sets of fields later.
   */
  listAllObjectFieldGroups$(objectType: ObjectType, objectSubtype?: string): Observable<CrmFieldGroupInterface[]> {
    if ((objectType === 'CustomObject' || objectType === 'Ticket') && !objectSubtype) {
      return of([]);
    }

    return this.fieldGroupsTarget$().pipe(
      switchMap((target) => {
        const requestKey = this.generateRequestKey(target, objectType, objectSubtype);

        const cached = this.fieldGroupsRequestMap.get(requestKey);
        if (cached) {
          return cached;
        }

        const fieldGroups$ = this.createFieldGroupsObservable(target, objectType, objectSubtype).pipe(
          shareReplay({ bufferSize: 1, refCount: true }),
        );

        this.fieldGroupsRequestMap.set(requestKey, fieldGroups$);
        return fieldGroups$;
      }),
    );
  }

  /**
   * Resolves which namespace(s) field groups load from, re-emitting when that changes.
   *
   * Field groups are per-namespace and this service is a root-injector singleton, so the lookup has
   * to stay live: switching account groups in Business App pushes a new namespace through
   * `namespace$` without rebuilding the service, and callers must get the new namespace's groups.
   */
  private fieldGroupsTarget$(): Observable<FieldGroupsTarget> {
    const multiLocationContext$ = this.multiLocationConfig.multiLocationContext$ ?? of({} as MultiLocationContext);
    return combineLatest([this.config.namespace$, multiLocationContext$]).pipe(
      map(([namespace, context]) => ({
        namespace,
        multiLocationNamespaces:
          namespace === '' && context.groupId && context.accountGroupsIds?.length ? context.accountGroupsIds : [],
      })),
      distinctUntilChanged(
        (a, b) =>
          a.namespace === b.namespace &&
          a.multiLocationNamespaces.length === b.multiLocationNamespaces.length &&
          a.multiLocationNamespaces.every((ns, i) => ns === b.multiLocationNamespaces[i]),
      ),
    );
  }

  private createFieldGroupsObservable(
    target: FieldGroupsTarget,
    objectType: ObjectType,
    objectSubtype?: string,
  ): Observable<CrmFieldGroupInterface[]> {
    return of({
      fieldSchemas: [],
      pagingMetadata: {
        nextCursor: '',
        hasMore: true,
      },
    }).pipe(
      expand((resp: ListFieldGroupsResponseInterface) => {
        if (!resp?.pagingMetadata?.hasMore) {
          return EMPTY;
        }
        return this.listObjectFieldGroupsNextPage$(
          target,
          objectType,
          {
            cursor: resp.pagingMetadata.nextCursor,
            pageSize: MAX_SERVER_PAGE_SIZE,
          },
          objectSubtype,
        );
      }),
      reduce((acc: CrmFieldGroupInterface[], val) => {
        return [...acc, ...(val?.fieldGroups || [])];
      }, []),
      catchError(() => of([])),
    );
  }

  private listObjectFieldGroupsNextPage$(
    target: FieldGroupsTarget,
    objectType: ObjectType,
    paging: PagedRequestOptionsInterface,
    objectSubtype?: string,
  ): Observable<ListFieldGroupsResponseInterface> {
    const isPotentialContact = objectType === 'PotentialContact';
    const lookupType: ObjectType = isPotentialContact ? 'Contact' : objectType;
    const request = {
      crmObjectType: lookupType,
      pagingOptions: paging,
      crmObjectSubtype: objectSubtype || '',
    };
    const response$: Observable<ListFieldGroupsResponseInterface> = target.multiLocationNamespaces.length
      ? this.crmMultiLocationFieldLayoutService.listMultiLocationFieldGroups({
          namespaces: target.multiLocationNamespaces,
          ...request,
        })
      : this.crmFieldLayoutApiService.listFieldGroups({ namespace: target.namespace, ...request });
    if (isPotentialContact) {
      return response$.pipe(map((resp) => suffixPotentialContactFieldGroupIds(resp)));
    }
    return response$;
  }

  private crmFieldGroupToFormFieldGroup(crmFieldGroup: CrmFieldGroupInterface): FieldGroupInterface {
    return {
      fieldGroupId: crmFieldGroup.fieldGroupId,
      description: crmFieldGroup.description,
      fieldIds: crmFieldGroup.fieldIds || [],
    };
  }

  listAllObjectFormFieldGroups$(
    objectType: ObjectType,
    objectSubtype?: Observable<string>,
  ): Observable<FieldGroupInterface[]> {
    return (objectSubtype ?? of('')).pipe(
      switchMap((subtype) => this.listAllObjectFieldGroups$(objectType, subtype)),
      map((crmFieldGroups) => crmFieldGroups.map(this.crmFieldGroupToFormFieldGroup)),
    );
  }

  private crmFieldSchemasToFormFieldSchemas(crmFieldSchemas: CrmFieldSchemaInterface[]): FieldSchemaInterface[] {
    return crmFieldSchemas.map((crmFieldSchema) => {
      return {
        fieldId: crmFieldSchema.fieldId || '',
        fieldType: crmFieldSchema.fieldType || FieldType.FIELD_TYPE_INVALID,
        fieldName: crmFieldSchema.fieldName || '',
        fieldDescription: crmFieldSchema.fieldDescription || '',
        dropdownOptions: crmFieldSchema.dropdownOptions || [],
        currencyCode: crmFieldSchema.currencyCode || '',
        value: null,
        readonly: crmFieldSchema.readonly,
        namespace: crmFieldSchema.namespace,
      } as FieldSchemaInterface;
    });
  }

  listAllFormFields$(objectType: ObjectType, objectSubtype?: string): Observable<FieldSchemaInterface[]> {
    return this.listAllObjectFields$(objectType, objectSubtype).pipe(
      map((crmFieldSchemas) => this.crmFieldSchemasToFormFieldSchemas(crmFieldSchemas)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private getCrmFieldValueToFormValue(crmFieldValue: CrmFieldValueInterface, fieldType: FieldType): any {
    if (isTimerFieldType(fieldType)) {
      return (crmFieldValue as FieldValueWithTimer).timerValue ?? null;
    }
    switch (fieldType) {
      case FieldType.FIELD_TYPE_INTEGER:
        return crmFieldValue.integerValue;
      case FieldType.FIELD_TYPE_CURRENCY:
        return crmFieldValue.currencyValue;
      case FieldType.FIELD_TYPE_STRING:
      case FieldType.FIELD_TYPE_DROPDOWN:
      case FieldType.FIELD_TYPE_EMAIL:
        return crmFieldValue.stringValue;
      case FieldType.FIELD_TYPE_PHONE:
        if (crmFieldValue.phoneFieldValues) {
          return crmFieldValue.phoneFieldValues;
        }
        return crmFieldValue.stringValue;
      case FieldType.FIELD_TYPE_BOOLEAN:
        return crmFieldValue.booleanValue;
      case FieldType.FIELD_TYPE_TAG:
      case FieldType.FIELD_TYPE_STRING_LIST:
        return crmFieldValue.stringValues?.values || [];
      case FieldType.FIELD_TYPE_DATETIME:
      case FieldType.FIELD_TYPE_DATE:
        return crmFieldValue.dateValue;
      case FieldType.FIELD_TYPE_FLOAT:
        return crmFieldValue.floatValue;
      default:
        return null;
    }
  }

  isModifiedFieldValue(
    currentValue: CrmFieldValueInterface,
    modifiedValue: CrmFieldValueInterface,
    fieldType: FieldType,
  ): boolean {
    return isModifiedFieldValue(currentValue, modifiedValue, fieldType);
  }

  private initializeFormFieldSchemaWithCrmObject(
    fieldSchema: FieldSchemaInterface,
    crmObject: CrmObjectInterface,
  ): void {
    if (!fieldSchema?.fieldId || !crmObject?.fields) {
      return;
    }

    const crmObjectFieldValue = crmObject.fields.find((crmObjectField) => {
      return crmObjectField.fieldId === fieldSchema.fieldId;
    });

    if (!crmObjectFieldValue) {
      return;
    }

    fieldSchema.value = this.getCrmFieldValueToFormValue(crmObjectFieldValue, fieldSchema.fieldType);
  }

  listAllFormFieldsInitializedWithCrmObject$(
    objectType: ObjectType,
    crmObjectId: string,
    objectSubtype?: string,
  ): Observable<FieldSchemaInterface[]> {
    return forkJoin([
      this.crmObjectService.getMultiObject(objectType, [crmObjectId], [
        { filterType: 1, fromFieldType: FieldType.FIELD_TYPE_PHONE, toFieldType: FieldType.FIELD_TYPE_PHONE },
      ] as ObjectProjectionFilterInterface[]),
      this.listAllFormFields$(objectType, objectSubtype),
    ]).pipe(
      map(([getMultiResponse, fieldSchemas]) => {
        if (getMultiResponse?.crmObjects?.length === 0 || !getMultiResponse?.crmObjects?.[0].crmObjectId) {
          throw new Error(this.translationService.instant('CRM_FORM_SERVICE.RESOURCE_NOT_FOUND_ERROR'));
        } else {
          const crmObject = getMultiResponse.crmObjects[0];
          fieldSchemas.forEach((field) => {
            this.initializeFormFieldSchemaWithCrmObject(field, crmObject);
          });
        }
        return fieldSchemas;
      }),
    );
  }

  validateCrmObject$(
    objectType: ObjectType,
    crmObject: CrmObjectInterface,
  ): Observable<ValidateErrorsWithFieldSchemas> {
    const validationErrors$ = this.crmObjectService
      .validateCrmObject(objectType, crmObject)
      .pipe(map((response) => response.errors || []));
    const fieldSchemaMap$ = this.listAllFormFields$(objectType, crmObject.crmObjectSubtype).pipe(
      map((schemas) => {
        const schemaMap = new Map<string, FieldSchemaInterface>();
        for (const schema of schemas) {
          schemaMap.set(schema.fieldId, schema);
        }
        return schemaMap;
      }),
    );
    return forkJoin([validationErrors$, fieldSchemaMap$]).pipe(
      map(([validationErrors, fieldSchemaMap]) => {
        return {
          errors: validationErrors,
          fieldSchemaMap: fieldSchemaMap,
        };
      }),
    );
  }

  validateAndCreateCrmObject$(
    objectType: ObjectType,
    fields: FieldInterface[],
    objectSubtype?: string,
  ): Observable<CrmObjectInterface | null> {
    return of(null).pipe(
      map(() => fields.filter((field) => field.hasModification()).map((field) => field.fieldValue())),
      map((fields) =>
        fields.filter((fieldValue) => {
          return fieldValue?.stringValue !== '';
        }),
      ),
      switchMap((fieldValues) => {
        const crmObject: CrmObjectInterface = {
          fields: fieldValues,
          crmObjectSubtype: objectSubtype,
        };
        const validationResponse$ = this.validateCrmObject$(objectType, crmObject);
        return forkJoin([of(fieldValues), validationResponse$]);
      }),
      switchMap(([fieldValues, validationResponse]) => {
        const validationErrorMessages$ = this.handleValidationErrors$(objectType, validationResponse);

        return combineLatest([of(fieldValues), validationErrorMessages$, of(validationResponse)]);
      }),

      switchMap(([fieldValues, validationErrors, validationResponse]) => {
        if (validationErrors) {
          throw new CrmObjectValidationError(validationErrors, validationResponse.errors);
        }
        if (!fieldValues?.length) {
          return of(null);
        }
        const crmObject: CrmObjectInterface = {
          fields: fieldValues,
          crmObjectSubtype: objectSubtype,
        };

        return this.crmObjectService.createCrmObject(objectType, crmObject).pipe(
          map((resp) => {
            crmObject.crmObjectId = resp.crmObjectId;
            return crmObject;
          }),
        );
      }),
    );
  }

  validateAndUpdateCrmObject$(
    objectType: ObjectType,
    crmObjectId: string,
    fields: FieldInterface[],
    objectSubtype?: string,
  ): Observable<CrmObjectInterface | null> {
    return this.validateCrmObject$(objectType, {
      crmObjectId: crmObjectId,
      fields: fields.map((field) => field.fieldValue()),
      crmObjectSubtype: objectSubtype,
    }).pipe(
      switchMap((validationResponse) => {
        return combineLatest([this.handleValidationErrors$(objectType, validationResponse), of(validationResponse)]);
      }),
      switchMap(([validationErrors, validationResponse]) => {
        if (validationErrors) {
          throw new CrmObjectValidationError(validationErrors, validationResponse.errors);
        }

        const modifiedFieldValues = fields
          .filter((field) => field.hasModification())
          .map((field) => field.fieldValue());
        if (!modifiedFieldValues?.length) {
          return of(null);
        }

        return this.crmObjectService
          .updateCrmObject(objectType, {
            crmObjectId: crmObjectId,
            fields: modifiedFieldValues,
            crmObjectSubtype: objectSubtype,
          })
          .pipe(
            map(() => ({
              crmObjectId: crmObjectId,
              fields: fields.map((field) => field.fieldValue()),
              crmObjectSubtype: objectSubtype,
            })),
          );
      }),
    );
  }

  validateAndUpdateCrmObjectFields$(
    objectType: ObjectType,
    crmObjectId: string,
    modifiedValues: FieldValueInterface[] = [],
    objectSubtype?: string,
  ): Observable<CrmObjectInterface | null> {
    const fieldSchemaMap$: Observable<Map<string, FieldSchema>> = this.crmObjectService
      .getMultiFieldSchema(
        objectType,
        modifiedValues.map((f) => f.fieldId),
        objectSubtype,
      )
      .pipe(
        map((response) => (response?.fieldSchemas as FieldSchema[]) || []),
        map((schemas) => {
          const schemaMap = new Map<string, FieldSchema>();
          for (const s of schemas) {
            if (!s?.fieldId) {
              continue;
            }
            schemaMap.set(s.fieldId, s);
          }
          return schemaMap;
        }),
        shareReplay(1),
      );
    const currentCrmObject$ = this.crmObjectService
      .getMultiObject(objectType, [crmObjectId], [
        { filterType: 1, fromFieldType: FieldType.FIELD_TYPE_PHONE, toFieldType: FieldType.FIELD_TYPE_PHONE },
      ] as ObjectProjectionFilterInterface[])
      .pipe(
        take(1),
        map((response) => {
          const crmObjects = response.crmObjects || [];
          if (crmObjects.length === 0 || !crmObjects[0].crmObjectId) {
            return null;
          }
          return crmObjects[0];
        }),
        filter((crmObject) => !!crmObject),
        shareReplay(1),
      );
    const values$ = combineLatest([currentCrmObject$, fieldSchemaMap$]).pipe(
      map(([currentObject, fieldSchemaMap]) => {
        const currentObjectFields = currentObject?.fields || [];
        const fieldsToModify: { fieldValue: CrmFieldValueInterface; indexToModify: number }[] = [];
        for (const modifiedValue of modifiedValues) {
          if (!modifiedValue?.fieldId) {
            continue;
          }
          const currentValueIndex = currentObjectFields.findIndex(
            (fieldValue: CrmFieldValueInterface) => fieldValue?.fieldId === modifiedValue.fieldId,
          );
          const currentValue =
            currentValueIndex !== -1 ? currentObjectFields[currentValueIndex] : { fieldId: modifiedValue.fieldId };
          const fieldSchema = fieldSchemaMap.get(modifiedValue.fieldId);
          if (!fieldSchema) {
            continue;
          }
          if (this.isModifiedFieldValue(currentValue, modifiedValue, fieldSchema.fieldType)) {
            fieldsToModify.push({
              fieldValue: modifiedValue,
              indexToModify: currentValueIndex,
            });
          }
        }
        if (fieldsToModify.length === 0) {
          return {};
        }
        for (const fieldToModify of fieldsToModify) {
          if (fieldToModify.indexToModify === -1) {
            currentObjectFields.push(fieldToModify.fieldValue);
          } else {
            currentObjectFields[fieldToModify.indexToModify] = fieldToModify.fieldValue;
          }
        }
        return {
          combinedFieldForValidation: [...currentObjectFields],
          fieldsToModify: [...fieldsToModify],
        };
      }),
      shareReplay(1),
    );
    const validationResponse$ = values$.pipe(
      take(1),
      switchMap(({ combinedFieldForValidation, fieldsToModify }) => {
        if (!fieldsToModify?.length) {
          return of(null);
        }

        return this.validateCrmObject$(objectType, {
          crmObjectId: crmObjectId,
          fields: combinedFieldForValidation,
          crmObjectSubtype: objectSubtype,
        });
      }),
    );

    return combineLatest([validationResponse$, values$]).pipe(
      take(1),
      switchMap(([validationResponse, { fieldsToModify }]) => {
        if (validationResponse) {
          const modifiedFieldIds =
            fieldsToModify?.map((f) => f.fieldValue.fieldId).filter((id): id is string => !!id) || [];
          return combineLatest([
            this.handleValidationErrors$(objectType, validationResponse, modifiedFieldIds),
            values$,
          ]);
        }
        return combineLatest([of(''), values$]);
      }),
      switchMap(([validationErrors, { combinedFieldForValidation, fieldsToModify }]) => {
        if (validationErrors) {
          throw new Error(validationErrors);
        }
        if (!fieldsToModify?.length) {
          return of(null);
        }

        const crmObject: CrmObjectInterface = {
          crmObjectId: crmObjectId,
          fields: fieldsToModify.map((f) => f.fieldValue),
          crmObjectSubtype: objectSubtype,
        };
        return this.crmObjectService.updateCrmObject(objectType, crmObject).pipe(
          map(() => {
            crmObject.fields = combinedFieldForValidation;
            return crmObject;
          }),
        );
      }),
    );
  }
}
